import { Auth } from 'aws-amplify';
import React from 'react';
import { useSelector } from 'react-redux';
import {
  cookieToken,
  isPlayGroundMode,
  isAdminDashboard,
} from '../../src/utils/CookieToken';
import {
  orgIdKey,
  defaultOrgIdValue,
} from '../components/widgets/OrgIdDropdown/utils';
import { orgIdHeaderKey } from '../constants/appConst';
import { LifelineAutoLogoutEvent } from '../analytics/login-logout/events';
import { MetricsForJumboEvent } from '../analytics/constants';
import getStore from '../store/store';
import { redirectToLogin } from '../utils/UtilFunctions';

const baseUrl = `${window.location.origin}/api/crm_proxy`;

/* ------------------------------------------------------------------------
 * Opt-in helpers for the `resilient` option below. Nothing here runs unless
 * a caller passes `resilient: true`, so every existing endpoint keeps the
 * exact parse-and-reject behaviour it has always had.
 * ---------------------------------------------------------------------- */

// Reads the body once, tolerating a payload that isn't JSON at all — a gateway
// HTML error page, a 204, a truncated response. Letting the raw SyntaxError
// escape is what surfaces "Unexpected token < in JSON at position 0" to the
// user instead of an error they can act on.
const parseJsonBody = async (response) => {
  let text;
  try {
    text = await response.text();
  } catch (e) {
    return null;
  }
  if (!text || !text.trim()) return null;
  try {
    return JSON.parse(text);
  } catch (e) {
    // On a failure, keep the raw body so the caller's parser can still read a
    // plain-text reason out of it. On a 2xx it carries no data, so the caller
    // gets null and renders its empty state rather than the raw text posing
    // as a payload.
    return response.ok ? null : { rawBody: text };
  }
};

// Rejection value for a non-2xx response: the backend's own error payload,
// untouched, plus the HTTP status. `httpStatus` is used rather than `status`
// because backend payloads carry their own `status` field.
const buildApiError = (body, status) => {
  const payload =
    body && typeof body === 'object' && !Array.isArray(body) ? body : {};
  const apiError = { ...payload, httpStatus: status };
  if (Array.isArray(body)) apiError.errors = body;
  return apiError;
};

const handleSignout = () => {
  // FIXME: this function has been writeen multple time merge it
  Promise.all([
    Auth.currentAuthenticatedUser()
      .then(() => {
        return Auth.signOut();
      })
      .catch(() => {
        // do nothing
      }),
    fetch('/api/crm_proxy/lifeline_apis/auth/logout', {
      method: 'POST',
    }).then(() => {
      // send relative url with encoding as argument to redirectTologin
      redirectToLogin(
        encodeURIComponent(
          `${window.location.pathname}${window.location.search}`,
        ),
      );
    }),
  ]);
};

async function client(
  endpoint,
  {
    apiURL = baseUrl,
    data,
    token,
    headers: customHeaders,
    isFile = false,
    responseType = 'json',
    customOrgId = null,
    // Opt-in, per call. fetch has no timeout of its own, so without this a
    // stalled request leaves the caller's loading state spinning forever.
    timeout = 0,
    // Opt-in, per call: tolerate a non-JSON/empty body instead of throwing a
    // SyntaxError, resolve an empty 2xx as null instead of throwing on
    // `.data`, and reject with the backend payload plus its httpStatus.
    // Default false keeps the original behaviour for every other endpoint.
    resilient = false,
    ...customConfig
  } = {},
) {
  const headers = {};

  const orgIdHeaderValue =
    customOrgId ??
    (isAdminDashboard ? defaultOrgIdValue : sessionStorage.getItem(orgIdKey));

  if (!cookieToken && token) {
    headers['cognito-access-token'] = token;
  }

  if (data && !isFile) {
    headers['content-type'] = 'application/json';
  }

  const config = {
    method: data ? 'POST' : 'GET',
    ...customConfig,
    headers: {
      ...headers,
      ...customHeaders,
      [orgIdHeaderKey]: String(orgIdHeaderValue),
    },
  };

  if (data) {
    if (isFile) {
      config.body = data;
    } else {
      config.body = JSON.stringify(data);
    }
  }

  const { crm_user_id, email_id, name, external_user_id } =
    getStore()?.getState()?.authState?.userDetails || {};

  let playGroundEndPoint = null;
  if (isPlayGroundMode) {
    playGroundEndPoint = endpoint.replace('lifeline_apis', 'playground');
  }

  // A caller that brought its own signal keeps control of cancellation.
  let timeoutId = null;
  if (timeout > 0 && !config.signal && typeof AbortController !== 'undefined') {
    const controller = new AbortController();
    config.signal = controller.signal;
    timeoutId = setTimeout(() => controller.abort(), timeout);
  }

  return window
    .fetch(
      `${apiURL}/${isPlayGroundMode ? playGroundEndPoint : endpoint}`,
      config,
    )
    .then(async (response) => {
      if (response.status === 401) {
        LifelineAutoLogoutEvent({
          [MetricsForJumboEvent.customer_id]: external_user_id,
          [MetricsForJumboEvent.feature_meta]: {
            agent_id: external_user_id,
            crm_user_id,
            email_id,
            name,
          },
        });
        return handleSignout();
      }

      let responseData;
      if (responseType === 'blob') {
        responseData = await response.blob();
      } else if (resilient) {
        responseData = await parseJsonBody(response);
      } else {
        responseData = await response.json();
      }
      if (response.ok) {
        return resilient
          ? responseData?.data ?? responseData ?? null
          : responseData.data ?? responseData;
      }
      return Promise.reject(
        resilient ? buildApiError(responseData, response.status) : responseData,
      );
    })
    .finally(() => {
      if (timeoutId) clearTimeout(timeoutId);
    });
}

function useClient() {
  const token = useSelector((state) => state.authState?.userDetails?.authToken);

  return React.useCallback(
    (endpoint, config) =>
      client(endpoint, {
        ...config,
        token,
      }),
    [token],
  );
}

export { useClient };
